import React, { useEffect, useReducer, useState } from "react";
import {
    JsonHubProtocol,
    HubConnectionState,
    HubConnectionBuilder,
    LogLevel
} from '@microsoft/signalr';
import { notification } from "antd";
import moment from "moment";

export interface AppState {
    CargandoInfracciones: boolean,
    PorcentajeCargaInfracciones: number,
    UltimaCargaInfracciones?: moment.Moment,
    PrecargaConfiguracionProduccionNombre?: string,
    UrlCancelarPrecarga?: string,
}

// El contexto ahora es un diccionario de estados
export interface MultiAppState {
    instances: { [key: string]: AppState };
}

const AppStateContext = React.createContext<MultiAppState | undefined>(undefined);



export default AppStateContext;

export function AppStateProvider(props: { children: React.ReactNode }) {

    const hubConfigs = React.useMemo(() => { 
        return Object.entries(process.env) 
        .filter(([key]) => key
        .startsWith("REACT_APP_STATE_CONTEXT_HUB_")) 
        .map(([key, value]) => { 
            if (!value) return null; 
            const [hubUrl, cancelUrl] = value.split(","); 
            return { key, hubUrl, cancelUrl }; }) 
            .filter(Boolean) as { key: string; hubUrl: string; cancelUrl?: string }[]; 
        }, []);

    const [instances, setInstances] = useState<{ [key: string]: AppState }>(() => {
        return hubConfigs.reduce((acc, cfg, index) => { 
            acc[String(index + 1)] = { 
                CargandoInfracciones: false, 
                PorcentajeCargaInfracciones: 0, 
                UrlCancelarPrecarga: cfg.cancelUrl, 
            }; 
            
            return acc; 
        }, {} as { [key: string]: AppState });
    });

    const setupHub = (id: string, url: string | undefined) => {
        if (!url) return null;

        const connection = new HubConnectionBuilder()
            .withUrl(url)
            .withAutomaticReconnect()
            // .configureLogging(LogLevel.Information) // Activa esto si querés debuguear en consola
            .build();

        connection.on("Changed:CargandoInfracciones", (state: boolean) => {
            setInstances(prev => ({
                ...prev,
                [id]: { ...prev[id], CargandoInfracciones: state }
            }));
        });

        connection.on("Changed:PorcentajeCargaInfracciones", (state: number) => {
            setInstances(prev => ({
                ...prev,
                [id]: { ...prev[id], PorcentajeCargaInfracciones: state }
            }));
        });

        connection.on("Changed:UltimaCargaInfracciones", (state: string) => {
            setInstances(prev => ({
                ...prev,
                [id]: { ...prev[id], UltimaCargaInfracciones: state ? moment(state) : undefined }
            }));
        });

        connection.on("Changed:PrecargaConfiguracionProduccionNombre", (state: string) => {
            setInstances(prev => ({
                ...prev,
                [id]: { ...prev[id], PrecargaConfiguracionProduccionNombre: state }
            }));
        });

        connection.start().catch(err => console.error(`Error Hub ${id} (${url}):`, err));
        return connection;
    };

    useEffect(() => {

        const connections = hubConfigs.map((cfg, index) => setupHub(String(index + 1), cfg.hubUrl));

        return () => { connections.forEach(conn => conn?.stop()); };
    }, []);

    return (
        <AppStateContext.Provider value={{ instances }}>
            {props.children}
        </AppStateContext.Provider>
    );
}
